// The single place where camelCase form values from EditIncidentModal are
// mapped to the snake_case shape the API expects. Both the create and update
// handlers in index.tsx delegate here, so the two payloads can never drift
// apart — and the mapping is unit-testable without rendering the page.
import { IncidentCreatePayload, IncidentUpdatePayload } from 'core/incidents/types';
import { EditIncidentValues, EffectiveFieldConfig } from './components/EditIncidentModal';

/**
 * Fields shared by the create and update payloads. Conditional fields are
 * nulled when their category config hides them, so a value left behind in the
 * form can never be persisted for a field the user could not see.
 *
 * NOTE on `values.critical`: it is the EFFECTIVE critical flag, not the raw
 * checkbox. EditIncidentModal submits `critical: isCriticalIncident`
 * (checkbox OR critical-by-default category), so a forced-critical category
 * arrives here as `true` even when the box was never ticked. Deliberately not
 * re-derived from the category here — that would put the rule in two places and
 * let them drift. `EffectiveFieldConfig` carries no `isCriticalCategory` for
 * exactly this reason.
 */
const sharedFields = (
    values: EditIncidentValues,
    cfg: EffectiveFieldConfig,
    showRemedy: boolean,
    isRetail: boolean,
) => ({
    incident_problem_category_id: Number(values.problemCategory),
    // Retail incidents have no priority field, so never persist one for them.
    // Guard the empty case too: Number('') is 0, which would post an invalid id
    // if form state were ever stale — send null so the API's required-unless-retail
    // rule reports "priority required" rather than "invalid priority".
    incident_priority_id: (!isRetail && values.priority) ? Number(values.priority) : null,
    incident_status_id: Number(values.status),
    description: values.description,
    is_critical: values.critical,
    // Only asked on critical incidents; cleared otherwise so a downgraded
    // incident can't keep a stale answer.
    mitie_duty_manager_informed: values.critical ? values.mitieDutyManagerInformed || null : null,
    remedy_reference: showRemedy ? values.remedyReference || null : null,
    maximo_reference: cfg.showMaximoReference ? values.maximoReference || null : null,
    khc_reference: cfg.showKhcReference ? values.khcReference || null : null,
    concept_reference: cfg.showConceptReference ? values.conceptReference || null : null,
    police_reference: cfg.showPoliceReference && values.policeAssigned === 'yes' ? values.policeReference || null : null,
    police_assigned: cfg.showPoliceReference && values.policeAssigned ? values.policeAssigned === 'yes' : null,
    reason: cfg.showPoliceReference && values.policeAssigned === 'no' ? values.reason || null : null,
});

/**
 * Build the update payload for an existing incident.
 */
export const buildIncidentUpdatePayload = (
    values: EditIncidentValues,
    cfg: EffectiveFieldConfig,
    showRemedy: boolean,
    isRetail: boolean,
    // Supplied only when closing the incident (collected via the Mark as
    // Complete modal). The API completes the incident with them and ignores
    // them otherwise.
    rootCause: string | null = null,
    resolutionDetails: string | null = null,
): IncidentUpdatePayload => ({
    ...sharedFields(values, cfg, showRemedy, isRetail),
    checked_in_at: values.checkedInAt || null,
    checked_out_at: values.checkedOutAt || null,
    root_cause: rootCause || null,
    resolution_details: resolutionDetails || null,
    // Always sent explicitly: omitting it makes the API fall back to notifying
    // the distribution list, which would defeat the opt-in.
    send_to_distribution: values.sendToDistribution,
});

/**
 * Build the create payload for a new incident.
 */
export const buildIncidentCreatePayload = (
    values: EditIncidentValues,
    cfg: EffectiveFieldConfig,
    showRemedy: boolean,
    isRetail: boolean,
): IncidentCreatePayload => ({
    site_id: Number(values.site),
    ...sharedFields(values, cfg, showRemedy, isRetail),
});
